import { GraphQLProvider } from '@codezo/graphql';
import { ReactNode } from 'react';

/**
 * Subscriptions go over one WebSocket per tab unless an app's env sets
 * VITE_SUBSCRIPTION_TRANSPORT=sse — see `subscriptionTransport` on GraphQLProvider.
 */
const SUBSCRIPTION_TRANSPORT: 'ws' | 'sse' =
	import.meta.env.VITE_SUBSCRIPTION_TRANSPORT === 'sse' ? 'sse' : 'ws';

interface GraphQLWithAuthProps {
	children: ReactNode;
	uri: string;
	token?: string;
	audience?: string;
	onGuestPreviewBlocked?: (message: string) => void;
	reauthOnUnauthorized: boolean;
}

/**
 * Inner component that has access to auth context
 */
function GraphQLWithAuth({
	children,
	uri,
	token,
	audience,
	onGuestPreviewBlocked,
	reauthOnUnauthorized,
}: GraphQLWithAuthProps) {
	const handleUnauthorized = () => {
		if (!reauthOnUnauthorized) return;
		// Instead of logging out immediately, redirect to re-authentication page
		const currentPath = window.location.pathname;

		// Don't redirect if we're already on auth pages or if we're already redirecting
		if (
			currentPath !== '/auth/reauth' &&
			currentPath !== '/auth/callback' &&
			currentPath !== '/auth/login' &&
			!window.location.href.includes('/auth/reauth')
		) {
			// Pass the return path as a hash parameter like ReAuthenticateCallback expects
			window.location.replace(`/auth/reauth#prevPath=${encodeURIComponent(currentPath)}`);
		}
	};

	return (
		<GraphQLProvider
			uri={uri}
			token={token || ''}
			audience={audience}
			onUnauthorized={handleUnauthorized}
			onGuestPreviewBlocked={onGuestPreviewBlocked}
			subscriptionTransport={SUBSCRIPTION_TRANSPORT}
		>
			{children}
		</GraphQLProvider>
	);
}

export interface AuthGraphQLProviderProps {
	children: ReactNode;
	uri: string;
	token?: string;
	/**
	 * Calling app's audience identifier — sent as `x-app-audience` on every
	 * request, used by the auth subgraph to scope refresh-token cookies
	 * per app. Set this to the app's name (e.g. 'admin', 'vetter',
	 * 'organization', 'manufacturer').
	 *
	 * Without this, login / refresh / logout will be rejected by the backend.
	 */
	audience?: string;
	/**
	 * A mutation refused because the session is an admin's read-only guest
	 * preview. Only the client app can be in one; other apps pass nothing.
	 */
	onGuestPreviewBlocked?: (message: string) => void;
	/**
	 * Send the user to /auth/reauth when a request comes back unauthenticated (the default). The public
	 * site turns it off: it has no re-auth flow, and a wrong sign-in or 2FA code answers UNAUTHENTICATED,
	 * which its own screens show as an error.
	 */
	reauthOnUnauthorized?: boolean;
}

/**
 * Combined provider that sets up both Auth and GraphQL contexts
 * with proper integration between them.
 *
 * This provider:
 * 1. Sets up GraphQL client first (for auth mutations)
 * 2. Wraps with AuthProvider
 * 3. Re-wraps with GraphQL client that has auth token access
 *
 * This ensures:
 * - Auth mutations work properly
 * - Auth tokens are included in all requests
 * - Unauthorized errors trigger logout
 */
export function AuthGraphQLProvider({
	children,
	uri,
	token,
	audience,
	onGuestPreviewBlocked,
	reauthOnUnauthorized = true,
}: AuthGraphQLProviderProps) {
	return (
		<GraphQLWithAuth
			uri={uri}
			token={token}
			audience={audience}
			onGuestPreviewBlocked={onGuestPreviewBlocked}
			reauthOnUnauthorized={reauthOnUnauthorized}
		>
			{children}
		</GraphQLWithAuth>
	);
}
